import React, { useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { Portal } from 'react-portal';
import Accordion from '@/components/Accordion/Accordion';
import AccordionItem from '@/components/Accordion/AccordionItem';
import Button from '@/components/Button/Button';
import useNotify from '@/hooks/useNotify';
import useSessionInfo from '@/hooks/useSessionInfo';
import CodeVerificationModal, {
  VerificationFlow,
} from './CodeVerificationModal';
import ConfirmPasswordModal from './ConfirmPasswordModal';
import RowValue from './RowValue';
import { REMOVE_PHONE, SECURITY_SETTINGS } from './Security.queries';
import { formatPhone } from './formatPhone';

type Open =
  | { modal: 'verify'; flow: VerificationFlow }
  | { modal: 'removePhone' };

const MyAccountSection: React.FC = () => {
  const client = useApolloClient();
  const { showSuccess } = useNotify();
  const email = useSessionInfo().session?.userProfile?.user?.email;
  const { data } = useQuery(SECURITY_SETTINGS);
  const security = data?.securitySettings;
  const [open, setOpen] = useState<Open>();
  const close = () => setOpen(undefined);

  if (!email) return null;

  const phoneSendsCodes = security?.twoFactorMethod === 'sms';
  const startRemovingPhone = () =>
    setOpen(
      phoneSendsCodes && !security?.emailVerified
        ? { modal: 'verify', flow: 'removePhone' }
        : { modal: 'removePhone' }
    );

  const removePhone = async (password: string) => {
    await client.mutate({
      mutation: REMOVE_PHONE,
      variables: { password },
      refetchQueries: [SECURITY_SETTINGS],
      awaitRefetchQueries: true,
    });
    close();
    showSuccess('Mobile phone removed');
  };

  return (
    <>
      <Accordion title="My Account" variant="onMain">
        <AccordionItem
          label="Email"
          value={
            security?.hasPassword ? (
              <RowValue text={email}>
                <Button
                  variant="outline"
                  icon="edit"
                  onClick={() => setOpen({ modal: 'verify', flow: 'setEmail' })}
                >
                  Edit
                </Button>
              </RowValue>
            ) : (
              <span>{email}</span>
            )
          }
        />
        {security?.hasPassword && (
          <AccordionItem
            label="Mobile phone"
            value={
              security.phone ? (
                <RowValue text={formatPhone(security.phone)}>
                  <Button
                    variant="outline"
                    icon="edit"
                    onClick={() =>
                      setOpen({ modal: 'verify', flow: 'setPhone' })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    icon="trash"
                    onClick={startRemovingPhone}
                  >
                    Remove
                  </Button>
                </RowValue>
              ) : (
                <RowValue text="None">
                  <Button
                    variant="outline"
                    onClick={() =>
                      setOpen({ modal: 'verify', flow: 'setPhone' })
                    }
                  >
                    Add
                  </Button>
                </RowValue>
              )
            }
          />
        )}
      </Accordion>
      <Portal>
        {open?.modal === 'verify' && (
          <CodeVerificationModal flow={open.flow} onClose={close} />
        )}
        {open?.modal === 'removePhone' && (
          <ConfirmPasswordModal
            title="Remove mobile phone"
            description={
              phoneSendsCodes
                ? 'Your phone number will be removed and two-factor codes will go to your email.'
                : 'Your phone number will be removed from your account.'
            }
            confirmLabel="Remove phone"
            onConfirm={removePhone}
            onClose={close}
          />
        )}
      </Portal>
    </>
  );
};

export default MyAccountSection;
